import { DataGridPro, DataGridProProps } from "@mui/x-data-grid-pro";
import { styled } from "@mui/material/styles";

interface DataGridUIProps extends DataGridProProps {
  isInAIAvatarLayout?: boolean;
}

const DataGridUI = styled(DataGridPro)<DataGridUIProps>(
  ({ theme, isInAIAvatarLayout }) => {
    // Determine header colors based on layout context and theme
    const getHeaderBackground = () => {
      if (isInAIAvatarLayout) {
        return theme.palette.mode === 'light' ? '#000000' : '#ffffff';
      }
      return theme.palette.primary.main;
    };

    const getHeaderTextColor = () => {
      if (isInAIAvatarLayout) {
        return theme.palette.mode === 'light' ? '#ffffff' : '#000000';
      }
      return theme.palette.primary.contrastText || '#fff';
    };

    return {
      // New row highlighting
      "& .super-app-theme--New": {
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(250, 250, 250, 0.08)' : 'rgba(35, 35, 35, 0.04)',
        "&:hover": {
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(250, 250, 250, 0.12)!important' : 'rgba(35, 35, 35, 0.08)!important',
        },
      },
      // Column Headers
      "& .MuiDataGrid-columnHeaders": {
        backgroundColor: getHeaderBackground(),
        color: getHeaderTextColor(),
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
      "& .MuiDataGrid-columnHeader .MuiCheckbox-root svg": {
        color: getHeaderTextColor(),
      },
      "& .MuiDataGrid-menuIconButton": {
        color: getHeaderTextColor(),
      },
      "& .MuiDataGrid-columnHeaderTitleContainer .MuiDataGrid-sortIcon": {
        color: getHeaderTextColor(),
      },
    // Pinned columns
    "& .MuiDataGrid-pinnedColumns--left .MuiDataGrid-columnHeaders": {
      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[200],
      color: theme.palette.text.primary,
    },
    // Row hover effect
    "& .MuiDataGrid-row:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    // Cell borders and styling
    "& .MuiDataGrid-cell": {
      borderBottom: `1px solid ${theme.palette.divider}`,
      color: theme.palette.text.primary,
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-cell:focus": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-cell:focus-visible": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-cell:focus-within": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    // Grid background
    backgroundColor: theme.palette.background.paper,
    // Footer
    "& .MuiDataGrid-footerContainer": {
      backgroundColor: theme.palette.background.paper,
      borderTop: `1px solid ${theme.palette.divider}`,
    },

    // Remove ALL focus borders and outlines from the entire DataGrid
    "&:focus": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "&:focus-visible": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "&:focus-within": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    
    // Remove focus from the root grid element
    "& .MuiDataGrid-root": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-root:focus": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-root:focus-visible": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-root:focus-within": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    
    // Remove focus from the main grid container
    "& .MuiDataGrid-main": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-main:focus": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-main:focus-visible": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    
    
    // Remove focus from column headers
    "& .MuiDataGrid-columnHeader": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-columnHeader:focus": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-columnHeader:focus-visible": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-columnHeader:focus-within": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    
    // Remove focus from rows
    "& .MuiDataGrid-row": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-row:focus": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-row:focus-visible": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-row:focus-within": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    
    // Remove focus from virtual scroller
    "& .MuiDataGrid-virtualScroller": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-virtualScroller:focus": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-virtualScroller:focus-visible": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    
    // Remove focus from any other grid elements
    "& *:focus": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& *:focus-visible": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& *:focus-within": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    };
  }
);

export default DataGridUI;
